import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Coins, AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export const fmt = (v, dec = 2) =>
  v === null || v === undefined || isNaN(v)
    ? "—"
    : Number(v).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
export const cls = (v) =>
  Number(v) < 0 ? "text-rose-600 dark:text-rose-400"
    : Number(v) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400";
const hoyISO = () => new Date().toISOString().slice(0, 10);
const diasAtras = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const aYmd = (iso) => Number(iso.replaceAll("-", ""));
const aUnix = (iso, finDelDia) => Math.floor(new Date(`${iso}T${finDelDia ? "23:59:59" : "00:00:00"}`).getTime() / 1000);

/** Consulta puntual a un endpoint del módulo. Devuelve [data, cargando, ejecutar, error] */
export function useConsulta(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const ejecutar = async (params) => {
    setLoading(true); setError(null);
    try {
      const { data: r } = await axios.get(`${API}/pppoker/consulta/${path}`, { ...auth(), params });
      if (r.ok) { setData(r.data); setError(null); }
      else { setData(null); setError(`${r.error} (código ${r.error_code})`); toast.error(r.error); }
    } catch (e) {
      setData(null); setError("Falló la consulta al backend"); toast.error("Falló la consulta");
    } finally { setLoading(false); }
  };
  return [data, loading, ejecutar, error];
}

export const Panel = ({ titulo, extra, children, testid }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden" data-testid={testid}>
    <div className="px-3 py-2 border-b dark:border-gray-700 flex items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{titulo}</h2>
      {extra}
    </div>
    {children}
  </div>
);

const Metrica = ({ label, valor, pp, testid }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-3">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-lg font-bold tabular-nums ${pp ? cls(valor) : "text-gray-900 dark:text-gray-100"}`} data-testid={testid}>
      {fmt(valor)} {pp && <span className="text-xs font-normal text-gray-400">PP</span>}
    </p>
  </div>
);

export const Err = ({ error }) => error ? (
  <div className="rounded-lg border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3 text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2">
    <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
  </div>
) : null;

const Boton = ({ onClick, loading, children, testid }) => (
  <button onClick={onClick} disabled={loading} data-testid={testid}
    className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 flex items-center gap-2">
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {children}
  </button>
);

const Campo = ({ label, children }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = "px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600";

export const SinClub = () => (
  <div className="py-16 text-center text-gray-500">Elegí un club arriba</div>
);

export const Baneado = ({ v }) => v
  ? <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400"><ShieldAlert className="w-4 h-4" /> suspendido</span>
  : <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" /> normal</span>;

export const JUEGOS = ["evchop", "jackpot", "luckydraw", "colorgame", "russian_poker", "crashgame", "others"];
export const JUEGO_LABEL = {
  evchop: "EV Chop", jackpot: "Jackpot", luckydraw: "Lucky Draw", colorgame: "Color Game",
  russian_poker: "Russian Poker", crashgame: "Crash Game", others: "Otros (NLH, PLO, MTT, SpinUp…)",
};
export const BAN_TIPO = { 0: "normal", 1: "ban manual", 2: "desbaneado", 3: "auto-ban" };

/* ============================================================== JUGADOR */
export function TabJugador({ clubId }) {
  const [uid, setUid] = useState("");
  const [desde, setDesde] = useState(diasAtras(7));
  const [hasta, setHasta] = useState(hoyISO());
  const [tz, setTz] = useState("-0500");
  const [saldo, loadSaldo, verSaldo, errSaldo] = useConsulta("saldo");
  const [det, loadDet, verDet, errDet] = useConsulta("jugador");

  if (!clubId) return <SinClub />;

  return (
    <div className="space-y-4" data-testid="api-pppoker-jugador">
      <div className="flex flex-wrap items-end gap-2">
        <Campo label="uid del jugador">
          <input value={uid} onChange={(e) => setUid(e.target.value.replace(/\D/g, ""))}
            placeholder="65056" className={`${inputCls} w-32`} data-testid="jugador-uid" />
        </Campo>
        <Boton onClick={() => uid && verSaldo({ club_id: clubId, uid })} loading={loadSaldo} testid="jugador-ver-saldo">
          Ver saldo
        </Boton>
        <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 mx-1" />
        <Campo label="Desde">
          <input type="date" value={desde} max={hoyISO()} onChange={(e) => setDesde(e.target.value)}
            className={inputCls} data-testid="jugador-desde" />
        </Campo>
        <Campo label="Hasta">
          <input type="date" value={hasta} max={hoyISO()} onChange={(e) => setHasta(e.target.value)}
            className={inputCls} data-testid="jugador-hasta" />
        </Campo>
        <Campo label="Zona horaria del corte">
          <select value={tz} onChange={(e) => setTz(e.target.value)} className={inputCls} data-testid="jugador-tz">
            <option value="-0500">Hora de la unión (UTC-05:00) — como la app</option>
            <option value="-0300">Argentina (UTC-03:00)</option>
          </select>
        </Campo>
        <Boton onClick={() => uid && verDet({ club_id: clubId, uid, start_day: aYmd(desde), end_day: aYmd(hasta), utc_offset: tz })}
          loading={loadDet} testid="jugador-ver-detalle">
          Datos de juego
        </Boton>
      </div>
      <p className="text-xs text-gray-500">
        El rango no puede pasar de 31 días ni ser anterior a ~3 meses (límite de PPPoker). La app oficial
        corta los días con la <b>hora de la unión (UTC-05:00)</b>: si elegís Argentina los totales dan distinto.
      </p>

      <Err error={errSaldo} />
      {saldo && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg p-4 text-white">
            <p className="text-xs opacity-80 flex items-center gap-1"><Coins className="w-3 h-3" /> Saldo en el club</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="jugador-saldo">{fmt(saldo.balance)} PP</p>
          </div>
          <Metrica label="uid" valor={saldo.uid} />
          <Metrica label="club_id" valor={saldo.club_id} />
        </div>
      )}

      <Err error={errDet} />
      {det && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Metrica label="Ganancias (total)" valor={det.total_profit} pp testid="jugador-total-profit" />
            <Metrica label="Cuota / rake" valor={det.fee} pp testid="jugador-fee" />
            <Metrica label="Entrada de SpinUp" valor={det.spinup_buyin} pp testid="jugador-spinup" />
            <Metrica label="Ganancias MTT" valor={det.mtt_profit} pp />
            <Metrica label="Manos (sin MTT/SNG/Spin)" valor={det.hands_cnt} />
            <Metrica label="BB/100 (ring)" valor={det.bb_profit} />
          </div>
          <p className="text-xs text-gray-500">
            Son los datos <b>del jugador</b>: si el uid es un agente, acá va sólo lo que jugó él, no lo de su equipo.
            El «hasta» no puede ser un día futuro (PPPoker devuelve error 10002).
          </p>
        </div>
      )}
    </div>
  );
}

/* ============================================================== CLUBES */
/* ============================================================== AGENTES */
export function TabAgentes({ clubId }) {
  const [agentUid, setAgentUid] = useState("");
  const [data, loading, ejecutar, error] = useConsulta("agente");
  if (!clubId) return <SinClub />;

  return (
    <div className="space-y-4" data-testid="api-pppoker-agentes">
      <div className="flex flex-wrap items-end gap-2">
        <Campo label="uid del agente o superagente">
          <input value={agentUid} onChange={(e) => setAgentUid(e.target.value.replace(/\D/g, ""))}
            placeholder="65056" className={`${inputCls} w-32`} data-testid="agente-uid" />
        </Campo>
        <Boton onClick={() => agentUid && ejecutar({ club_id: clubId, agent_uid: agentUid })}
          loading={loading} testid="agente-consultar">Consultar agente</Boton>
      </div>
      <p className="text-xs text-gray-500">
        La API no lista los agentes: hay que saber el uid. Si el uid no es agente de ese club devuelve error 10000.
        La semana y el día se calculan con la <b>hora de la unión</b> (la misma que usa la app oficial),
        no con la hora de Argentina.
      </p>
      <Err error={error} />
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metrica label="Esta semana (total)" valor={data.profit_week} pp testid="agente-profit-week" />
            <Metrica label="Hoy (total)" valor={data.profit_day} pp testid="agente-profit-day" />
            <Metrica label="Semana pasada" valor={data.profit_last_week} pp />
            <Metrica label="Ayer" valor={data.profit_last_day} pp />
          </div>
          <p className="text-xs text-gray-500">
            «Esta semana (total)» es el mismo número que muestra la app oficial en <b>Ganancias</b>.
            «Resultado del período» es el que PPPoker usa para el tope: sólo suma los tipos de juego
            marcados en la última columna de la tabla.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-3">
              <p className="text-xs text-gray-500">Estado</p>
              <p className="text-sm font-semibold mt-1"><Baneado v={data.suspend_status} /></p>
            </div>
            <Metrica label="Resultado del período" valor={data.current_profit} pp />
            <Metrica label="Tope de pérdida" valor={data.loss_limit} pp />
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-3">
              <p className="text-xs text-gray-500">Auto-ban / modo</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">
                {data.autoban ? "activado" : "desactivado"} · {data.limit_type === 1 ? "diario" : "semanal"}
              </p>
            </div>
          </div>
          <Panel titulo="Desglose por tipo de juego" testid="agente-juegos">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Juego</th><th className="px-3 py-2 text-right">Semana</th>
                  <th className="px-3 py-2 text-right">Hoy</th><th className="px-3 py-2 text-right">Semana pasada</th>
                  <th className="px-3 py-2 text-right">Ayer</th><th className="px-3 py-2 text-center">Cuenta para el tope</th>
                </tr>
              </thead>
              <tbody>
                {JUEGOS.map((g) => (
                  <tr key={g} className="border-t dark:border-gray-700">
                    <td className="px-3 py-1.5">{JUEGO_LABEL[g]}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${cls(data[`${g}_weekly_profit`])}`}>{fmt(data[`${g}_weekly_profit`])}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${cls(data[`${g}_daily_profit`])}`}>{fmt(data[`${g}_daily_profit`])}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${cls(data[`${g}_last_weekly_profit`])}`}>{fmt(data[`${g}_last_weekly_profit`])}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${cls(data[`${g}_last_daily_profit`])}`}>{fmt(data[`${g}_last_daily_profit`])}</td>
                    <td className="px-3 py-1.5 text-center">
                      {g === "others" ? "—" : (data[`limit_${g}`] ? "sí" : "no")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              Lo que la tarjeta «Datos del agente» de la app tiene y la API no devuelve
            </p>
            <ul className="text-xs text-amber-800/90 dark:text-amber-200/90 mt-1 space-y-0.5 list-disc pl-5">
              <li><b>Cuota</b> (rake del equipo) y <b>Entrada de SpinUp</b>: existen sólo <b>por jugador</b>, en el tab Jugador. La API no permite listar el equipo del agente, así que no se puede sumar solo.</li>
              <li><b>Saldo de crédito</b>, <b>Fichas de miembro invitado</b> y <b>Fichas en el juego</b>: no están en ningún endpoint (agent/stats devuelve 49 campos y ninguno es de crédito).</li>
              <li><b>Porcentaje del RC</b> y <b>Retorno de cuota</b>: tampoco existen en la API.</li>
              <li>Esos datos hoy salen de la planilla que se importa en <b>Agentes United</b>. Están pedidos a PPPoker.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================== SUPER UNIÓN */
const FilaOrg = ({ o }) => (
  <tr className="border-t dark:border-gray-700">
    <td className="px-3 py-1.5">
      <span className="font-medium text-gray-900 dark:text-gray-100">{o.name || o.league_name}</span>
      <span className="text-gray-400 font-mono ml-1">{o.league_id || o.super_league_id}</span>
    </td>
    <td className="px-3 py-1.5"><Baneado v={o.is_banned} /></td>
    <td className="px-3 py-1.5">{o.limit_type === 1 ? "diario" : "semanal"}</td>
    <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${cls(o.current_profit)}`}>{fmt(o.current_profit)}</td>
    <td className={`px-3 py-1.5 text-right tabular-nums ${cls(o.weekly_profit ?? o.evchop_weekly_profit)}`}>
      {fmt(o.weekly_profit ?? o.evchop_weekly_profit)}
    </td>
    <td className={`px-3 py-1.5 text-right tabular-nums ${cls(o.daily_profit ?? o.evchop_daily_profit)}`}>
      {fmt(o.daily_profit ?? o.evchop_daily_profit)}
    </td>
    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(o.loss_limit)}</td>
  </tr>
);

const BloqueOrg = ({ titulo, path, clubId, nota }) => {
  const [data, loading, ejecutar, error] = useConsulta(path);
  const info = data?.info || [];
  return (
    <Panel titulo={titulo} testid={`superunion-${path}`}
      extra={<Boton onClick={() => ejecutar({ club_id: clubId })} loading={loading} testid={`superunion-${path}-btn`}>Consultar</Boton>}>
      <div className="p-3 space-y-3">
        <p className="text-xs text-gray-500">{nota}</p>
        <Err error={error} />
        {info.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Organización</th><th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Modo</th><th className="px-3 py-2 text-right">Resultado actual</th>
                  <th className="px-3 py-2 text-right">Semana</th><th className="px-3 py-2 text-right">Día</th>
                  <th className="px-3 py-2 text-right">Tope pérdida</th>
                </tr>
              </thead>
              <tbody>{info.map((o, i) => <FilaOrg key={i} o={o} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
};

export function TabSuperUnion({ clubId }) {
  if (!clubId) return <SinClub />;
  return (
    <div className="space-y-4" data-testid="api-pppoker-superunion">
      <BloqueOrg titulo="Superliga · ligas que la componen" path="super-league" clubId={clubId}
        nota="Si el club no pertenece a una superliga, PPPoker devuelve el error 10003." />
      <BloqueOrg titulo="PPSR · miembros" path="ppsr" clubId={clubId}
        nota="A nivel PPSR sólo se separan EV Chop y Jackpot; el resto va en «otros». Los importes ya vienen convertidos a la tasa de la organización." />
      <BloqueOrg titulo="PPST · miembros" path="ppst" clubId={clubId}
        nota="PPST sólo informa el agregado semanal y diario, sin desglose por juego." />
    </div>
  );
}

/* ============================================================== MOVIMIENTOS */
export function TabMovimientos({ clubId }) {
  const [desde, setDesde] = useState(diasAtras(7));
  const [hasta, setHasta] = useState(hoyISO());
  const [flow, loadFlow, verFlow, errFlow] = useConsulta("flow");
  const [cert, loadCert, verCert, errCert] = useConsulta("certificados");
  if (!clubId) return <SinClub />;

  return (
    <div className="space-y-4" data-testid="api-pppoker-movimientos">
      <div className="flex flex-wrap items-end gap-2">
        <Campo label="Desde">
          <input type="date" value={desde} max={hoyISO()} onChange={(e) => setDesde(e.target.value)}
            className={inputCls} data-testid="flow-desde" />
        </Campo>
        <Campo label="Hasta">
          <input type="date" value={hasta} max={hoyISO()} onChange={(e) => setHasta(e.target.value)}
            className={inputCls} data-testid="flow-hasta" />
        </Campo>
        <Boton onClick={() => verFlow({ club_id: clubId, start: aUnix(desde), end: aUnix(hasta, true) })}
          loading={loadFlow} testid="flow-consultar">Traer movimientos</Boton>
        <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 mx-1" />
        <Boton onClick={() => verCert({ club_id: clubId })} loading={loadCert} testid="cert-consultar">
          Miembros certificados
        </Boton>
      </div>
      <p className="text-xs text-gray-500">
        Máximo 14 días por consulta y el rango tiene que caer dentro de los últimos 90 días (regla de PPPoker).
      </p>

      <Err error={errFlow} />
      {flow && (
        <Panel titulo={`Envíos y recepciones de fichas · ${flow.length} registro(s)`} testid="flow-tabla">
          {flow.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">
              Sin movimientos en el rango. Puede ser que este historial registre sólo lo hecho vía API
              (es una de las dudas abiertas con PPPoker).
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha</th><th className="px-3 py-2 text-left">id</th>
                    <th className="px-3 py-2 text-left">uid</th><th className="px-3 py-2 text-left">Liga</th>
                    <th className="px-3 py-2 text-right">Monto (PP)</th>
                  </tr>
                </thead>
                <tbody>
                  {flow.map((m) => (
                    <tr key={m.id} className="border-t dark:border-gray-700">
                      <td className="px-3 py-1.5 whitespace-nowrap">{m.insert_time_desc}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-400">{m.id}</td>
                      <td className="px-3 py-1.5 font-mono">{m.uid}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-400">{m.league_id}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${cls(m.amount)}`}>{fmt(m.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      <Err error={errCert} />
      {cert && (
        <Panel titulo={`Miembros certificados · ${cert.length}`} testid="cert-lista">
          <div className="p-3">
            {cert.length === 0 ? (
              <p className="text-sm text-gray-500">
                Ninguno. «Certificado» es una verificación propia de PPPoker, no equivale a «todos los miembros»
                (la API no permite listar el total de miembros de un club).
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {cert.map((u) => (
                  <span key={u} className="px-2 py-0.5 text-xs font-mono rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                    {u}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
